import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

// Connect to emulator in development
if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch (error) {
    // Already connected
  }
}

/**
 * Fortnox OAuth2 Authentication Service
 */
class FortnoxAuth {
  constructor() {
    // Get these from environment variables or Firebase config
    this.clientId = process.env.REACT_APP_FORTNOX_CLIENT_ID || '';
    // NOTE: clientSecret is NEVER stored or used in client code for security
    // It's only used server-side in Cloud Functions
    
    // Redirect URI must match EXACTLY what is registered in Fortnox Developer Portal.
    // Set REACT_APP_FORTNOX_REDIRECT_URI to the exact URL (e.g. https://app.svenry.com/fortnox/callback).
    this.redirectUri = process.env.REACT_APP_FORTNOX_REDIRECT_URI
      || (typeof window !== 'undefined' ? `${window.location.origin}/fortnox/callback` : 'https://app.svenry.com/fortnox/callback');
    
    // Debug: Log what redirect URI is being used
    console.log('🔍 Fortnox Auth - Redirect URI:', this.redirectUri);
    console.log('🔍 Fortnox Auth - Hostname:', window.location.hostname);
    console.log('🔍 Fortnox Auth - Origin:', window.location.origin);
    
    // Scope - includes file attachments and archive access
    this.scope = process.env.REACT_APP_FORTNOX_SCOPE || 'companyinformation supplierinvoice connectfile archive';
  
    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeFortnoxCode');
    this.saveTokensFunction = httpsCallable(functions, 'saveFortnoxTokens');
    this.getStatusFunction = httpsCallable(functions, 'getFortnoxStatus');
    this.refreshTokenFunction = httpsCallable(functions, 'refreshFortnoxToken');
  }
  
    /**
     * Generate secure state parameter for OAuth flow
     * Uses crypto.getRandomValues for cryptographically secure random values
     */
    generateSecureState() {
      // Generera en säker, unik state-sträng med crypto API
      const array = new Uint32Array(4);
      if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(array);
        return Array.from(array, dec => dec.toString(36)).join('');
      } else {
        // Fallback om crypto API inte är tillgänglig
        return Math.random().toString(36).substring(2, 15) + 
               Math.random().toString(36).substring(2, 15) + 
               Date.now().toString(36);
      }
    }

    /**
     * Generate authorization URL
     */
    getAuthorizationUrl(state = null, scopeOverride = null) {
      if (!this.clientId) {
        throw new Error('Fortnox Client ID not configured');
      }
    
      // Try scope override first, then configured scope, then default
      const scope = scopeOverride || this.scope || 'companyinformation';
    
      // Generera säker state om ingen angavs
      const stateValue = state || this.generateSecureState();
      
      // Spara state i sessionStorage för validering senare
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem('fortnox_oauth_state', stateValue);
        console.log('🔒 Saved OAuth state to sessionStorage:', stateValue);
      }
    
      // Debug: Log what redirect URI is being used
      console.log('🔍 DEBUG - Redirect URI being used:', this.redirectUri);
      console.log('🔍 DEBUG - Redirect URI type:', typeof this.redirectUri);
      console.log('🔍 DEBUG - Redirect URI length:', this.redirectUri.length);
      console.log('🔍 DEBUG - Window location origin:', window.location.origin);
      console.log('🔍 DEBUG - Window location hostname:', window.location.hostname);
    
      const params = new URLSearchParams({
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        response_type: 'code',
        scope: scope,
        state: stateValue,
        access_type: 'offline',
      });
    
      const authUrl = `https://apps.fortnox.se/oauth-v1/auth?${params.toString()}`;
      console.log('🔍 DEBUG - Full authorization URL:', authUrl);
      console.log('🔍 DEBUG - URLSearchParams redirect_uri:', params.get('redirect_uri'));
      console.log('🔍 DEBUG - Encoded redirect_uri:', encodeURIComponent(this.redirectUri));
      return authUrl;
    }
  /**
   * Exchange authorization code for access token (via Cloud Function)
   */
  async exchangeCodeForToken(code, state) {
    try {
      const result = await this.exchangeCodeFunction({
        code: code,
        redirectUri: this.redirectUri,
        state: state, // Skicka state för backend-validering
      });
      
      return {
        accessToken: result.data.accessToken,
        refreshToken: result.data.refreshToken,
        expiresIn: result.data.expiresIn,
      };
    } catch (error) {
      console.error('Error exchanging code for token:', error);
      throw error;
    }
  }

  /**
   * Save tokens to Firestore via Cloud Function
   * Note: clientSecret is handled server-side, never sent from client
   */
  async saveTokens(tokens) {
    try {
      const result = await this.saveTokensFunction({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresIn: tokens.expiresIn,
        // clientSecret removed - handled server-side only for security
      });
      return result.data;
    } catch (error) {
      console.error('Error saving tokens:', error);
      throw error;
    }
  }

  /**
   * Get Fortnox connection status
   */
  async getConnectionStatus() {
    try {
      const result = await this.getStatusFunction();
      return result.data;
    } catch (error) {
      console.error('Error getting Fortnox status:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    try {
      const result = await this.refreshTokenFunction();
      
      // Save new tokens
      await this.saveTokens({
        accessToken: result.data.accessToken,
        refreshToken: result.data.refreshToken || null,
        expiresIn: result.data.expiresIn,
      });
      
      console.log('Token refreshed successfully');
      return result.data;
    } catch (error) {
      console.error('Error refreshing token:', error);
      // If refresh fails, user needs to reconnect
      throw new Error('Token refresh failed. Please reconnect your Fortnox account.');
    }
  }

  /**
   * Refresh access token if expired or about to expire
   * @param {number} bufferSeconds - Refresh if expires within this many seconds (default: 300 = 5 min)
   */
  async refreshTokenIfNeeded(bufferSeconds = 300) {
    try {
      const status = await this.getConnectionStatus();
      
      if (!status.connected) {
        throw new Error('Fortnox not connected');
      }

      // Check if token is expired or will expire soon
      const now = Date.now();
      const expiresAt = status.tokenExpiresAt;
      const bufferMs = bufferSeconds * 1000;
      
      if (expiresAt && (now + bufferMs) >= expiresAt) {
        console.log('Token expired or expiring soon, refreshing...');
        await this.refreshAccessToken();
      }
      
      return true;
    } catch (error) {
      console.error('Error checking/refreshing token:', error);
      throw error;
    }
  }

  /**
   * Connect Fortnox account (complete OAuth flow)
   */
  async connect() {
    // Generera säker state och spara den
    const state = this.generateSecureState();
    const authUrl = this.getAuthorizationUrl(state);
    window.location.href = authUrl;
  }
}

// Fixed: Assign to variable before exporting
const fortnoxAuth = new FortnoxAuth();
export default fortnoxAuth;